import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { Trophy, Crown, Users2, Flame } from "lucide-react";

const RANK_COLORS = { core: "#22C55E", prime: "#3B82F6", elite: "#A855F7", supreme: "#EAB308" };

function Podium({ items, valueKey, valueFmt, label }) {
  const top3 = items.slice(0, 3);
  if (top3.length === 0) return <div className="text-slate-500 text-center py-6">No data yet.</div>;
  const [second, first, third] = [top3[1], top3[0], top3[2]];
  const Card = ({ item, rank, height, medal }) => item ? (
    <div className={`flex flex-col items-center flex-1`}>
      <div className="text-3xl mb-2">{medal}</div>
      <div className="rounded-t-2xl border border-white/10 bg-[#0F1626] w-full flex flex-col items-center justify-end p-4 relative" style={{height, background: rank === 1 ? "linear-gradient(180deg, rgba(138,226,52,0.1), rgba(15,22,38,1))" : "#0F1626"}}>
        <div className="h-12 w-12 rounded-full bg-nb-cyan/20 flex items-center justify-center text-nb-cyan font-bold text-lg mb-2 border-2" style={{borderColor: RANK_COLORS[item.rank]}}>{item.display_name?.[0]?.toUpperCase() || "?"}</div>
        <div className="text-white font-heading text-sm text-center">{item.display_name}</div>
        <div className="text-[10px] uppercase font-bold" style={{color: RANK_COLORS[item.rank]}}>{item.rank}</div>
        <div className="font-mono text-[#8AE234] mt-1 text-sm">{valueFmt(item[valueKey])}</div>
      </div>
    </div>
  ) : <div className="flex-1"/>;
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 tracking-widest mb-3">Top 3 · {label}</div>
      <div className="flex items-end gap-3">
        <Card item={second} rank={2} height={160} medal="🥈"/>
        <Card item={first} rank={1} height={200} medal="🥇"/>
        <Card item={third} rank={3} height={140} medal="🥉"/>
      </div>
    </div>
  );
}

function Table({ items, valueKey, valueFmt, valueLabel, extraCol }) {
  return (
    <div className="nb-card p-0 overflow-x-auto mt-6">
      <table className="w-full text-sm">
        <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
          <tr>
            <th className="text-left px-3 py-3 w-12">#</th>
            <th className="text-left px-3 py-3">Investor</th>
            <th className="text-left px-3 py-3">Rank</th>
            {extraCol && <th className="text-right px-3 py-3">{extraCol.label}</th>}
            <th className="text-right px-3 py-3">{valueLabel}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {items.length === 0 && <tr><td colSpan={extraCol ? 5 : 4} className="text-center py-8 text-slate-500">No data yet.</td></tr>}
          {items.map((it, i) => (
            <tr key={it.id} data-testid={`leader-row-${i}`}>
              <td className="px-3 py-3 font-mono text-slate-400">{i + 1}</td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-nb-cyan/20 flex items-center justify-center text-nb-cyan text-xs font-bold">{it.display_name?.[0]?.toUpperCase() || "?"}</div>
                  <div className="text-white">{it.display_name}</div>
                </div>
              </td>
              <td className="px-3 py-3 text-xs uppercase font-bold" style={{color: RANK_COLORS[it.rank]}}>{it.rank}</td>
              {extraCol && <td className="px-3 py-3 text-right font-mono text-slate-300">{extraCol.render(it)}</td>}
              <td className="px-3 py-3 text-right font-mono text-[#8AE234]">{valueFmt(it[valueKey])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Leaderboard() {
  const [tab, setTab] = useState("earners");
  const [data, setData] = useState({ earners: [], teams: [], weekly: [] });

  useEffect(() => {
    (async () => {
      try {
        const [e, t, w] = await Promise.all([
          api.get("/leaderboard/earners"),
          api.get("/leaderboard/teams"),
          api.get("/leaderboard/weekly"),
        ]);
        setData({ earners: e.data.leaders || [], teams: t.data.leaders || [], weekly: w.data.leaders || [] });
      } catch {}
    })();
  }, []);

  const tabs = [
    { key: "earners", label: "Top Earners", icon: Trophy, color: "#8AE234" },
    { key: "teams", label: "Biggest Teams", icon: Users2, color: "#00E5FF" },
    { key: "weekly", label: "Weekly Champions", icon: Flame, color: "#EAB308" },
  ];

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Community</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1 flex items-center gap-3"><Trophy className="text-[#EAB308]"/> Leaderboard</h1>
        <p className="text-slate-400 mt-2 text-sm">See who's crushing it in the Next Billion network.</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`leader-tab-${t.key}`} onClick={()=>setTab(t.key)} className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors flex items-center gap-2 ${tab===t.key?"bg-[#8AE234]/10 text-[#8AE234] border-[#8AE234]/40":"border-white/10 text-slate-300 hover:text-white"}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>

      <div className="nb-card p-6">
        {tab === "earners" && (
          <>
            <Podium items={data.earners} valueKey="total_earnings" valueFmt={(v)=>`$${(v||0).toFixed(2)}`} label="All-time earners"/>
            <Table items={data.earners} valueKey="total_earnings" valueLabel="Total Earnings" valueFmt={(v)=>`$${(v||0).toFixed(2)}`} extraCol={{label: "Invested", render: (r)=>`$${(r.active_investment||0).toFixed(0)}`}}/>
          </>
        )}
        {tab === "teams" && (
          <>
            <Podium items={data.teams} valueKey="team_volume" valueFmt={(v)=>`$${(v||0).toFixed(0)}`} label="Biggest teams"/>
            <Table items={data.teams} valueKey="team_volume" valueLabel="Team Volume" valueFmt={(v)=>`$${(v||0).toFixed(0)}`} extraCol={{label: "Directs", render: (r)=>`${r.active_directs}/${r.direct_count}`}}/>
          </>
        )}
        {tab === "weekly" && (
          <>
            <Podium items={data.weekly} valueKey="weekly_earnings" valueFmt={(v)=>`$${(v||0).toFixed(2)}`} label="Last 7 days"/>
            <Table items={data.weekly} valueKey="weekly_earnings" valueLabel="Last 7 days" valueFmt={(v)=>`$${(v||0).toFixed(2)}`}/>
          </>
        )}
      </div>
    </AppLayout>
  );
}
